<script lang="ts">
  import { tick } from 'svelte';
  import MarkdownContent, {
    type DocumentHeading,
  } from '../../lib/chat/MarkdownContent.svelte';
  import type { Palette } from '../../theme.svelte';

  let { content, palette }: { content: string; palette: Palette } = $props();

  let toc = $state<DocumentHeading[]>([]);
  let activeId = $state<string | null>(null);
  let tocOpen = $state(false);
  let paper = $state<HTMLElement | null>(null);
  let observer: IntersectionObserver | null = null;

  let hasToc = $derived(toc.length >= 2);

  function prefersReducedMotion(): boolean {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  }

  function handleHeadings(headings: DocumentHeading[]): void {
    toc = headings;
    if (headings.length < 2) {
      activeId = null;
      tocOpen = false;
    }
  }

  /** Resalta la sección visible dentro del contenedor de scroll de la página. */
  function connectObserver(): void {
    observer?.disconnect();
    observer = null;
    if (!paper || toc.length < 2) return;

    const root = paper.closest('.scroll-y') as HTMLElement | null;

    observer = new IntersectionObserver(
      (entries) => {
        const visibles = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visibles.length > 0) {
          activeId = visibles[0].target.id;
        }
      },
      { root, rootMargin: '-80px 0px -68% 0px', threshold: 0 },
    );

    for (const item of toc) {
      const heading = document.getElementById(item.id);
      if (heading) observer.observe(heading);
    }
  }

  function goTo(id: string): void {
    const heading = document.getElementById(id);
    if (!heading) return;

    heading.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start',
    });
    history.replaceState(null, '', `#${id}`);
    activeId = id;
    tocOpen = false;
  }

  // El índice se reconstruye cuando MarkdownContent publica los encabezados.
  $effect(() => {
    toc;
    void tick().then(connectObserver);
    return () => {
      observer?.disconnect();
      observer = null;
    };
  });

  // Enlace profundo: /biblioteca/123#articulo-5
  $effect(() => {
    if (toc.length === 0) return;
    const hash = decodeURIComponent(window.location.hash.replace(/^#/, ''));
    if (!hash) return;
    void tick().then(() => {
      const heading = document.getElementById(hash);
      if (heading) {
        heading.scrollIntoView({ behavior: 'auto', block: 'start' });
        activeId = hash;
      }
    });
  });
</script>

<div class="reader" class:with-toc={hasToc}>
  {#if hasToc}
    <aside class="toc" aria-label="Índice del documento">
      <p class="toc-title">Índice</p>
      <nav>
        <ol>
          {#each toc as item (item.id)}
            <li style={`--toc-level: ${item.level}`}>
              <button
                type="button"
                class:active={activeId === item.id}
                aria-current={activeId === item.id ? 'true' : undefined}
                title={item.text}
                onclick={() => goTo(item.id)}
              >
                {item.text}
              </button>
            </li>
          {/each}
        </ol>
      </nav>
    </aside>

    <div class="toc-mobile">
      <button
        type="button"
        class="toc-toggle"
        aria-expanded={tocOpen}
        onclick={() => (tocOpen = !tocOpen)}
      >
        <i class="bi bi-list-nested" aria-hidden="true"></i>
        Índice del documento
        <i class="bi {tocOpen ? 'bi-chevron-up' : 'bi-chevron-down'}" aria-hidden="true"></i>
      </button>

      {#if tocOpen}
        <nav class="toc-mobile-panel" aria-label="Índice del documento">
          {#each toc as item (item.id)}
            <button
              type="button"
              class:active={activeId === item.id}
              aria-current={activeId === item.id ? 'true' : undefined}
              style={`--toc-level: ${item.level}`}
              onclick={() => goTo(item.id)}
            >
              {item.text}
            </button>
          {/each}
        </nav>
      {/if}
    </div>
  {/if}

  <div class="paper" bind:this={paper}>
    <MarkdownContent
      content={content}
      {palette}
      variant="document"
      onHeadings={handleHeadings}
    />
  </div>
</div>

<style>
  .reader {
    width: 100%;
    min-width: 0;
  }

  .reader.with-toc {
    display: grid;
    grid-template-columns: minmax(180px, 230px) minmax(0, 1fr);
    gap: clamp(16px, 2.4vw, 32px);
    align-items: start;
  }

  .toc {
    position: sticky;
    top: 12px;
    max-height: calc(100dvh - 180px);
    overflow-y: auto;
    padding: 12px 10px;
    border: 1px solid var(--biblioteca-border);
    border-radius: 12px;
    background: var(--biblioteca-surface);
    scrollbar-width: thin;
  }

  .toc-title {
    margin: 0 0 8px 10px;
    color: var(--biblioteca-muted);
    font-size: 11px;
    font-weight: 750;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .toc ol {
    display: grid;
    gap: 1px;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .toc li {
    padding-left: calc((var(--toc-level, 2) - 2) * 10px);
  }

  .toc button,
  .toc-mobile-panel button {
    display: -webkit-box;
    width: 100%;
    overflow: hidden;
    border: 0;
    border-left: 2px solid transparent;
    border-radius: 0 6px 6px 0;
    background: transparent;
    color: var(--biblioteca-text);
    padding: 5px 8px;
    font: inherit;
    font-size: 12px;
    line-height: 1.35;
    text-align: left;
    cursor: pointer;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
  }

  .toc button:hover,
  .toc-mobile-panel button:hover {
    background: color-mix(in srgb, var(--biblioteca-accent) 9%, transparent);
  }

  .toc button.active,
  .toc-mobile-panel button.active {
    border-left-color: var(--biblioteca-accent);
    background: color-mix(in srgb, var(--biblioteca-accent) 12%, transparent);
    color: var(--biblioteca-accent);
    font-weight: 650;
  }

  .toc button:focus-visible,
  .toc-mobile-panel button:focus-visible,
  .toc-toggle:focus-visible {
    outline: 2px solid var(--biblioteca-accent);
    outline-offset: 1px;
  }

  .toc-mobile {
    display: none;
  }

  @media (max-width: 1000px) {
    .reader.with-toc {
      display: block;
    }

    .toc {
      display: none;
    }

    .toc-mobile {
      display: block;
      margin-bottom: 12px;
    }

    .toc-toggle {
      display: flex;
      width: 100%;
      align-items: center;
      gap: 8px;
      border: 1px solid var(--biblioteca-border);
      border-radius: 10px;
      background: var(--biblioteca-surface);
      color: var(--biblioteca-text);
      padding: 9px 12px;
      font: inherit;
      font-size: 13px;
      font-weight: 650;
      cursor: pointer;
    }

    .toc-toggle i:last-child {
      margin-left: auto;
    }

    .toc-mobile-panel {
      display: grid;
      gap: 2px;
      max-height: 320px;
      overflow-y: auto;
      margin-top: 6px;
      padding: 8px;
      border: 1px solid var(--biblioteca-border);
      border-radius: 10px;
      background: var(--biblioteca-surface);
    }

    .toc-mobile-panel button {
      padding-left: calc(8px + (var(--toc-level, 2) - 2) * 10px);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .toc button,
    .toc-mobile-panel button,
    .toc-toggle {
      transition: none;
    }
  }
</style>
